import { ApolloError } from "@apollo/client";
import {
  InputControl,
  InputPasswordControl,
  PasswordList,
  usePasswordPolicy,
} from "@aurem/components";
import { Button, Text } from "@aurem/ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { AuthContainer } from "components/AuthContainer";
import ROUTES from "const/routes";
import { useAcceptInvitation } from "hooks/useAcceptInvitation";
import useAnalytics, { AnalyticsEvent } from "hooks/useAnalytics";
import { useNavigateAfterInvitation } from "hooks/useNavigateAfterInvitation";
import { useProfile } from "pages/Profile/hooks/profile.hooks";
import { useSignUp } from "pages/SignUp/hooks/sign-up.hooks";
import { AuthStatus, useAuth } from "providers/AuthProvider";
import { useI18n } from "providers/I18nProvider";
import type { FC } from "react";
import { useState } from "react";

import { FormProvider, useForm, UseFormReturn } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSignOut } from "utils/auth";
import * as z from "zod";

enum Steps {
  Names,
  Password,
}

type PasswordFormProps = {
  form: UseFormReturn<{
    password: string;
    passwordConfirmation: string;
  }>;
  onSubmit: (data: {
    password: string;
    passwordConfirmation: string;
  }) => Promise<void>;
  error?: ApolloError | null;
  loading?: boolean;
};

const PasswordForm = ({
  form,
  onSubmit,
  loading,
  error,
}: PasswordFormProps) => {
  const { t } = useI18n();
  const { policy } = usePasswordPolicy();

  return (
    <FormProvider {...form}>
      <div className="flex flex-col gap-2 text-2xl mt-1">
        <h2 className="font-medium">
          {t("apps.admin.pages.signUpNewCompanyAdmin.page.setYourPassword")}
        </h2>
      </div>
      <form
        className="flex flex-col gap-6"
        noValidate
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div className="flex flex-col gap-5">
          <InputPasswordControl
            name="password"
            label={t(
              "apps.admin.pages.signUpNewCompanyAdmin.page.password.label",
            )}
            description={t(
              "apps.admin.pages.signUpNewCompanyAdmin.page.password.hint",
            )}
            type="password"
            data-e2e-id="signUp__input__password"
            required
          />
          <InputPasswordControl
            name="passwordConfirmation"
            label={t(
              "apps.admin.pages.signUpNewCompanyAdmin.page.passwordConfirmation.label",
            )}
            type="password"
            data-e2e-id="signUp__input__passwordConfirmation"
            required
          />
          {error && (
            <Text size="sm" weight="medium" className="text-danger-600">
              {error.message}
            </Text>
          )}
        </div>
        <PasswordList
          policy={policy}
          translations={{
            intro: t("apps.admin.validation.list.intro"),
            capitalLetter: t("apps.admin.validation.list.capitalLetter"),
            lowercaseLetter: t("apps.admin.validation.list.lowercaseLetter"),
            number: t("apps.admin.validation.list.number"),
            specialCharacter: t("apps.admin.validation.list.specialCharacter"),
            minimumLength: t(
              "apps.admin.validation.list.minimumLength",
              policy?.minLength,
            ),
          }}
        />
        <div className="flex flex-col gap-4">
          <Button
            data-e2e-id="signUp__button__submit"
            loading={loading}
            type="submit"
          >
            {t("apps.admin.pages.signUpNewCompanyAdmin.page.setPassword")}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

type NamesFormProps = {
  form: UseFormReturn<{
    fullName: string;
    preferredName: string;
  }>;
  onSubmit: () => void;
};

const NamesForm = ({ form, onSubmit }: NamesFormProps) => {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") ?? "";

  return (
    <FormProvider {...form}>
      <div className="flex flex-col gap-2 text-2xl mt-1">
        <h2 className="font-medium">
          {t("apps.admin.pages.signUpNewCompanyAdmin.page.emailVerified")}
        </h2>
        <Text size="sm">
          <span className="text-neutral-400">
            {t("apps.admin.pages.signUpNewCompanyAdmin.page.welcome")}
          </span>
        </Text>
        <Text size="sm" weight="medium" className="pt-6">
          {t("apps.admin.common.email")}
        </Text>
        <Text size="sm" weight="normal" className="pb-4">
          {email}
        </Text>

        <form
          className="flex flex-col gap-6"
          noValidate
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <InputControl
            data-e2e-id="signUp__input__fullName"
            label={t(
              "apps.admin.pages.signUpNewCompanyAdmin.page.fullName.label",
            )}
            name="fullName"
            required
          />
          <InputControl
            data-e2e-id="signUp__input__preferredName"
            label={t(
              "apps.admin.pages.signUpNewCompanyAdmin.page.preferredName.label",
            )}
            name="preferredName"
            description={t(
              "apps.admin.pages.signUpNewCompanyAdmin.page.preferredName.hint",
            )}
            required
          />
          <div className="flex flex-col gap-4">
            <Button data-e2e-id="signUp__button__submit" type="submit">
              {t("apps.admin.pages.signUpNewCompanyAdmin.page.nextStep")}
            </Button>
          </div>
        </form>
      </div>
    </FormProvider>
  );
};

const SignUpNewCompanyAdmin: FC = () => {
  const { language, t } = useI18n();
  const { language: languageParam } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuthState } = useAuth();
  const { signUp, loading, error } = useSignUp();
  const { upsertProfile, loadingUpsertProfile } = useProfile();
  const { track } = useAnalytics();
  const { validatePassword } = usePasswordPolicy();
  const { acceptInvitation } = useAcceptInvitation();
  const getDestination = useNavigateAfterInvitation();
  const signOut = useSignOut();

  const email = searchParams.get("email") ?? "";

  const [step, setStep] = useState<Steps>(Steps.Names);

  const namesFormSchema = z.object({
    fullName: z.string().min(1, t("apps.admin.validation.fullName.minLength")),
    preferredName: z
      .string()
      .min(1, t("apps.admin.validation.preferredName.minLength")),
  });

  const namesForm = useForm<z.infer<typeof namesFormSchema>>({
    resolver: zodResolver(namesFormSchema),
  });

  const passwordFormSchema = z
    .object({
      password: z.string(),
      passwordConfirmation: z.string(),
    })
    .refine((data) => data.password === data.passwordConfirmation, {
      message: t("apps.admin.validation.password.match"),
      path: ["passwordConfirmation"],
    });

  const passwordForm = useForm<z.infer<typeof passwordFormSchema>>({
    resolver: zodResolver(passwordFormSchema),
  });

  const onSubmitNamesForm = (): void => {
    setStep(Steps.Password);
  };

  const onSubmitPasswordForm = async ({
    password,
  }: z.infer<typeof passwordFormSchema>) => {
    const passwordError = validatePassword(password);
    if (passwordError) {
      passwordForm.setError("password", {
        type: "manual",
        message: passwordError,
      });
      return;
    }

    await signOut();
    const invitationId = searchParams.get("invitationId");
    let data;
    try {
      ({ data } = await signUp({
        variables: {
          email,
          password,
          isEmployee: false,
          invitationId,
        },
      }));
    } catch {
      return;
    }
    const tokens = data?.registerUser;
    if (tokens?.accessToken && tokens?.refreshToken) {
      setAuthState({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        status: AuthStatus.Authenticated,
      });
    }

    await upsertProfile({
      variables: {
        input: {
          name: namesForm.getValues("fullName").trim(),
          preferredName: namesForm.getValues("preferredName").trim(),
          userEmail: email || null,
          preferredLanguage: language,
        },
      },
      context: {
        headers: {
          Authorization: tokens?.accessToken,
        },
      },
    });

    track(AnalyticsEvent.SignUpByEmail);
    const invitationData = invitationId
      ? null
      : await acceptInvitation({
          accessToken: tokens?.accessToken,
        });

    const from = invitationData
      ? getDestination({ invitationData })
      : undefined;
    void navigate(`/${languageParam}/admin/${ROUTES.DISCLAIMERS}`, {
      state: { from },
    });
  };

  return (
    <AuthContainer platform="admin">
      {step === Steps.Names ? (
        <NamesForm form={namesForm} onSubmit={onSubmitNamesForm} />
      ) : (
        <PasswordForm
          form={passwordForm}
          onSubmit={onSubmitPasswordForm}
          error={error}
          loading={loading || loadingUpsertProfile}
        />
      )}
    </AuthContainer>
  );
};

export default SignUpNewCompanyAdmin;
